<template>
  <div class="eu-timeline">
    <div v-if="!groups.length" class="eu-empty text-muted small">No events configured for matches on this day yet.</div>

    <template v-else>
      <template v-for="group in groups" :key="group.application">
        <div class="eu-group">{{ group.application }}<span v-if="group.missingCount" class="eu-chip-warn">&#9888; {{ group.missingCount }} {{ group.missingCount === 1 ? 'event' : 'events' }} without channel</span></div>

        <template v-if="group.rows.length">
          <div class="eu-row" v-for="row in group.rows" :key="row.key">
            <div class="eu-label" :title="row.label"><span v-if="row.warn" class="eu-dot-warn" title="An event on this row has no channel configured"></span>{{ row.label }}</div>
            <div class="eu-track">
              <div
                  v-for="bar in row.bars"
                  v-show="bar.shadowWidth > 0"
                  :key="bar.calMatchId + '-shadow'"
                  class="eu-shadow"
                  :class="markClass(bar, bar.shadowCutLeft, false)"
                  :style="{ left: bar.shadowLeft + '%', width: bar.shadowWidth + '%' }"
                  @click="$emit('bar-click', bar.calMatchId)"
                  @mouseenter="showTip($event, bar)"
                  @mouseleave="hideTip"
              ></div>
              <div
                  v-for="bar in row.bars"
                  v-show="bar.width > 0"
                  :key="bar.calMatchId"
                  class="eu-bar"
                  :class="markClass(bar, bar.cutLeft, bar.cutRight)"
                  :style="{ left: bar.left + '%', width: bar.width + '%' }"
                  tabindex="0"
                  role="button"
                  :aria-label="bar.tooltip"
                  @click="$emit('bar-click', bar.calMatchId)"
                  @keydown.enter="$emit('bar-click', bar.calMatchId)"
                  @mouseenter="showTip($event, bar)"
                  @mouseleave="hideTip"
                  @focus="showTip($event, bar)"
                  @blur="hideTip"
              >{{ bar.label }}</div>
            </div>
          </div>
        </template>
        <div v-else class="eu-none">No events configured today</div>
      </template>

      <div class="eu-axis">
        <span v-for="h in axisHours" :key="h">{{ h }}</span>
      </div>

      <p class="eu-caption text-muted small">
        The faded lead-in before a bar is that event assignment's own setup time
        (<code>eventPadSetup</code>) before the match starts - there's no trailing pad, unlike
        Channel usage's shadow. A flat edge means the bar continues past this day's view. A
        dashed bar is an inactive event assignment, and an amber striped bar is an event with no
        channels linked yet. Bar color matches the same match's color
        in Channel usage above, so you can follow one match across both.
      </p>

      <div v-if="tip.visible" class="eu-tooltip" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
        <div v-for="(line, i) in tip.lines" :key="i" class="eu-tooltip-line">{{ line }}</div>
      </div>
    </template>
  </div>
</template>

<script>
// Presentational-only, same contract as ChannelUsageTimeline.vue: the parent (Daily.vue)
// computes everything - which applications/events actually have usage today, each bar's
// geometry as a percentage of the 24h window, and which match color slot each bar carries
// (the SAME slot ChannelUsageTimeline assigned that match, so one match reads as one color
// across both timelines) - this component only renders it and reports clicks.
export default {
  name: 'EventUsageTimeline',
  props: {
    // [{ application, missingCount, rows: [{ key, label, warn, bars: [{ calMatchId, left, width, shadowLeft,
    //   shadowWidth, cutLeft, cutRight, shadowCutLeft, colorSlot, inactive, noChannels, label, tooltip }] }] }]
    // One group per application (even one with no usage today, per Martin's mockup - the
    // "No events configured today" placeholder), one row per event actually used today
    // within it - a single event can carry multiple bars if more than one match uses that
    // slot on this day, at different times.
    groups: {
      type: Array,
      default: () => []
    }
  },
  emits: ['bar-click'],
  data() {
    return {
      axisHours: ['00', '02', '04', '06', '08', '10', '12', '14', '16', '18', '20', '22'],
      tip: { visible: false, x: 0, y: 0, lines: [] }
    };
  },
  methods: {
    markClass(bar, cutLeft, cutRight) {
      return {
        [`eu-series-${bar.colorSlot}`]: !bar.inactive && !bar.noChannels && !!bar.colorSlot,
        'eu-overflow': !bar.inactive && !bar.noChannels && !bar.colorSlot,
        'eu-nochannels': !bar.inactive && !!bar.noChannels,
        'eu-inactive': bar.inactive,
        'eu-cut-l': cutLeft,
        'eu-cut-r': cutRight
      };
    },
    showTip(evt, bar) {
      const targetRect = evt.currentTarget.getBoundingClientRect();
      const rootRect = this.$el.getBoundingClientRect();
      this.tip = {
        visible: true,
        x: targetRect.left - rootRect.left + targetRect.width / 2,
        y: targetRect.top - rootRect.top,
        lines: bar.tooltip.split('\n')
      };
    },
    hideTip() {
      this.tip.visible = false;
    }
  }
};
</script>

<style src="@/assets/style.css"></style>
